import { Outlet, NavLink, Link } from "react-router-dom";
import { Sparkles, Users, ShoppingBag, Calendar, Crown, Trophy, BookOpen } from "lucide-react";
import { Button } from "@/components/ui/button";
import CartDrawer from "@/components/CartDrawer";
import HeaderInstallButton from "@/components/HeaderInstallButton";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import SearchBar from "@/components/SearchBar";
import { useI18n } from "@/i18n/I18nProvider";

const PUBLIC_NAV = [
  { to: "/marketplace", labelKey: "nav.marketplace", icon: ShoppingBag },
  { to: "/events", labelKey: "nav.events", icon: Calendar },
  { to: "/community", labelKey: "nav.community", icon: Users },
  { to: "/leadership", labelKey: "nav.leaders", icon: Crown },
  { to: "/recognition", labelKey: "nav.stars", icon: Trophy },
  { to: "/guide", labelKey: "nav.guide", icon: BookOpen },

];

export default function PublicLayout() {
  const { t } = useI18n();
  return (
    <div className="min-h-screen bg-pearl flex flex-col">
      {/* Header */}
      <header className="sticky top-0 z-40 glass-header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16 gap-3">
          <Link to="/" className="flex items-center gap-2 flex-shrink-0" data-testid="public-brand-link">
            <img src="/icons/icon-192.png" alt="Shakti Wings Logo" className="w-9 h-9 rounded-lg object-contain" />
            <span className="text-xl sm:text-2xl font-serif font-semibold text-amethyst-800">{t("brand.name")}</span>
          </Link>
          <nav className="hidden md:flex items-center gap-6">
            {PUBLIC_NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`public-nav-${n.labelKey}`}
                className={({ isActive }) =>
                  `text-sm font-medium transition-colors flex items-center gap-1.5 ${
                    isActive ? "text-amethyst-800" : "text-gray-600 hover:text-amethyst-700"
                  }`
                }
              >
                <n.icon className="w-4 h-4" strokeWidth={1.5} />
                {t(n.labelKey)}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-2 sm:gap-3">
            <HeaderInstallButton />
            <LanguageSwitcher />
            <CartDrawer />
            <Link to="/auth" data-testid="public-login-btn" className="hidden sm:inline-block">
              <Button variant="ghost" className="text-amethyst-700 hover:bg-amethyst-50 text-sm">{t("nav.login")}</Button>
            </Link>
            <Link to="/auth?mode=signup" data-testid="public-signup-btn">
              <Button className="btn-magenta rounded-full px-4 sm:px-5 text-sm">{t("nav.join")}</Button>
            </Link>
          </div>
        </div>
        {/* Mobile nav strip */}
        <div className="md:hidden border-t border-amethyst-100 bg-white/60">
          <div className="max-w-7xl mx-auto px-2 flex items-center justify-around">
            {PUBLIC_NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`mobile-public-nav-${n.labelKey}`}
                className={({ isActive }) =>
                  `flex-1 flex flex-col items-center gap-1 py-2.5 text-xs font-medium ${
                    isActive ? "text-amethyst-700" : "text-gray-500"
                  }`
                }
              >
                <n.icon className="w-4 h-4" strokeWidth={1.5} />
                {t(n.labelKey)}
              </NavLink>
            ))}
          </div>
        </div>
        {/* Search bar — visible on all public pages */}
        <div className="border-t border-amethyst-100 bg-white/70 backdrop-blur">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 py-2">
            <SearchBar />
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="flex-1">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="bg-amethyst-900 text-amethyst-200 py-8 mt-12">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-gold-400" strokeWidth={1.5} />
            <span className="font-serif font-semibold text-white">{t("brand.name")}</span>
          </div>
          <p className="text-xs text-amethyst-300">© 2026 {t("brand.name")} · Empowering women across India</p>
        </div>
      </footer>
    </div>
  );
}
